/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { ReplaySubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';
import { countriesIso, CountryIso } from './quick-create-customer.interface';
import { DataPickerService } from '../../../core/services/data_picker/data_picker.service';
import { ContactInfoService } from '../../../components/contact-info/contact-info.component.service';
import { CreateForm, CreateFormField } from '../data-picker.interface';
import { TimezonePickerService } from 'app/core/services/timezone/timezone-picker.service';
import { PropertyService } from 'app/core/services/properties/properties.service';
import { CustomFieldConfig } from 'app/components/custom-field-form/custom-field-form.interface';
import { CustomFieldService } from 'app/components/custom-field-form/custom-field.service';
import { countries } from 'app/components/contact-info/contact-info.interface';


@Component({
    selector: 'quick-create-customer',
    templateUrl: './quick-create-customer.component.html',
    styleUrls: ['./quick-create-customer.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class QuickCreateCustomerComponent implements OnInit, OnChanges {
    @Input() formReset: boolean = false;
    @Input() formSubmit: boolean = false;
    @Input() prefillValues: any = null;
    @Input() formVisibility: boolean = false;
    @Input() fromModule: string = '';
    @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
    @Output() formValidationEmitter: EventEmitter<boolean> = new EventEmitter();

    customerForm: UntypedFormGroup = null;
    categories: Observable<any[]> = null;
    organizations: Observable<any[]> = null;
    categoryOptions: any[] = [];
    organizationOptions: any[] = [];
    timezones: Observable<any[]> = null;
    pagination: any = {
        loadingIndex: 1,
        totalPage: 1,
        keyword: ''
    };
    countries: CountryIso[] = countriesIso;
    countriesList: any[] = countries;
    loading: boolean = false;
    isFormLoaded: boolean = false;

    //custom fields
    customFieldConfig: CustomFieldConfig;
    showCustomFields: boolean = false;
    customFieldValue: any[] = [];
    currentCategoryId: string = '';
    customFieldOptions: any = {
      displayInColumn: 4,
      extraClass:'px-2'
    };

    createCustomer: CreateForm[] = [{
        headerName: 'Customer Information',
        headerDisplayKey: 'create.customer.customer.header',
        canShow: true,
        formFields: [{
            labelName: 'First Name',
            labelDisplayKey: 'text.first_name',
            fieldId: 'firstName',
            fieldType: 'input',
            inputType: 'text',
            formControlName: 'firstName',
            placeHolder: 'First Name',
            placeHolderDisplayKey: 'text.first_name',
            isMandatory: true,
            validationText: 'First Name is required',
            validationTextKey: 'create.customer.firstName.validText'
        }, {
            labelName: 'Last Name',
            labelDisplayKey: 'text.last_name',
            fieldId: 'lastName',
            fieldType: 'input',
            inputType: 'text',
            formControlName: 'lastName',
            placeHolder: 'Last Name',
            placeHolderDisplayKey: 'text.last_name'
        }, {
            labelName: 'Email',
            labelDisplayKey: 'email',
            fieldId: 'email',
            fieldType: 'input',
            inputType: 'email',
            formControlName: 'email',
            isMandatory: true,
            placeHolder: 'Email',
            placeHolderDisplayKey: 'text.email_placeholder',
            validationText: 'Email is required',
            validationTextKey: 'create.customer.email.validText',
            emailValidationText: 'Invalid email address',
            emailValidationTextKey: 'text.invalid_email_address'
        }, {
            labelName: 'Category',
            labelDisplayKey: 'category.text',
            fieldId: 'category',
            fieldType: 'autocomplete',
            inputType: 'text',
            formControlName: 'category',
            placeHolder: 'Choose Customer Category',
            placeHolderDisplayKey: 'create.customer.category.placeholder',
            observable: this.categories,
            oberservableKey: 'category_name'
        }, {
            labelName: 'Choose Organization',
            labelDisplayKey: 'choose.organization',
            fieldId: 'organization',
            fieldType: 'lazyautocomplete',
            inputType: 'text',
            formControlName: 'organization',
            placeHolder: 'Choose Organization',
            placeHolderDisplayKey: 'choose.organization',
            observable: this.organizations,
            oberservableKey: 'organization_name',
            oberservableValue: 'organization_uid',
            allowLoading: true,
            allowLazyLoading: true,
            valueSubject: new ReplaySubject<string | null>(),
            showIf: this.utilityService.hasModuleAccess('ORGANIZATION', false, 'from_sidebar')
        },
        {
            labelName: 'Preferred Timezone',
            labelDisplayKey: 'preferred.timezone',
            fieldId: 'customer_timezone',
            fieldType: 'dropdown',
            formControlName: 'customer_timezone',
            placeHolder: 'Preferred Timezone',
            placeHolderDisplayKey: 'preferred.timezone',
            observable: this.timezones,
            oberservableKey: 'display_name',
            oberservableValue: 'key',
            showIf: this.utilityService.isTimeZoneEnabled
        }
        ]
    }, {
        headerName: 'Contact Information',
        headerDisplayKey: 'create.customer.contact.header',
        headerType: 'contact',
        canShow: true,
        formFields: [{
            labelName: 'Home Number',
            labelDisplayKey: 'home',
            fieldId: 'home',
            fieldType: 'phoneNumber',
            inputType: 'text',
            formControlName: 'home',
            placeHolder: 'Home Number',
            placeHolderDisplayKey: 'text.home_number',
            cssClass: 'field-container-flex-basis'
        }, {
            labelName: 'Mobile Number',
            labelDisplayKey: 'text.mobile',
            fieldId: 'mobile',
            fieldType: 'phoneNumber',
            inputType: 'text',
            formControlName: 'mobile',
            placeHolder: 'Mobile Number',
            placeHolderDisplayKey: 'text.mobile_number',
            cssClass: 'field-container-flex-basis'
        }, {
            labelName: 'Work Number',
            labelDisplayKey: 'text.work',
            fieldId: 'work',
            fieldType: 'phoneNumber',
            inputType: 'text',
            formControlName: 'work',
            placeHolder: 'Work Number',
            placeHolderDisplayKey: 'text.work_number',
            cssClass: 'field-container-flex-basis'
        }]
    }, {
        headerName: 'Service Address',
        headerDisplayKey: 'text.service_address',
        headerType: 'address',
        formGroupName: 'serviceAddress',
        canShow: true,
        formFields: [],
        contactFields: {
            contactFirstName: false,
            contactLastName: false,
            contactEmail: false,
            contactPhone: false,
            street: true,
            enableMapsAutoComplete: true,
            canTruncate: true,
            landmark: false,
            city: true,
            state: true,
            country: true,
            zipcode: true,
            latitude: false,
            longitude: false
        }
    }, {
        headerName: 'Billing Address',
        headerDisplayKey: 'billing.address.text',
        headerType: 'address',
        formGroupName: 'billingAddress',
        canShow: true,
        isMatCheck: true,
        formControlName: 'sameAddress',
        matCheckLabel: 'text.same_as_service_address',
        formFields: [],
        contactFields: {
            contactFirstName: false,
            contactLastName: false,
            contactEmail: false,
            contactPhone: false,
            street: true,
            enableMapsAutoComplete: false,
            landmark: false,
            city: true,
            state: true,
            country: true,
            zipcode: true,
            latitude: false,
            longitude: false
        }
    }];
    formControls: any[] = [{
        formControlName: 'firstName',
        value: '',
        validators: [Validators.required]

    }, {
        formControlName: 'lastName',
        value: '',
        validators: []
    }, {
        formControlName: 'email',
        value: '',
        validators: [Validators.required, Validators.email]
    }, {
        formControlName: 'home',
        controls: [{
            formControlName: 'phoneNumber',
            value: '',
            validators: []
        }]
    }, {
        formControlName: 'mobile',
        controls: [{
            formControlName: 'phoneNumber',
            value: '',
            validators: []
        }]
    }, {
        formControlName: 'work',
        controls: [{
            formControlName: 'phoneNumber',
            value: '',
            validators: []
        }]
    }, {
        formControlName: 'organization',
        value: null,
        validators: []
    },{
        formControlName: 'customer_timezone',
        value: null,
        validators: []
    },{
        formControlName: 'category',
        value: null,
        validators: []
    }, {
        formControlName: 'serviceAddress',
        controls: []
    }, {
        formControlName: 'billingAddress',
        controls: []
    }, {
        formControlName: 'sameAddress',
        value: true,
        validators: []
    }];
    orgParamObj: any = {
        'count': 10,
        'page': 1,
        'filter.keyword': '',
        'sort': 'ASC',
        'sort_by': 'organization_name'
    };
    pickerAPIUrls: any;

    constructor(
        private utilityService: UtilityService,
        private dataCollectorService: DataCollectorService,
        private localisationService: LocalisationService,
        private pickerService: DataPickerService,
        private contactInfoService: ContactInfoService,
        private toast: HotToastService,
        public timezoneService: TimezonePickerService,
        private propertyService: PropertyService,
        private eleRef: ElementRef,
        private customFieldService: CustomFieldService) {
        this.customerForm = this.pickerService.createFormControls(this.formControls);
        this.customFieldConfig = {
            expansionDisabled: true,
            originalTemplate: true,
            module:'CUSTOMER',
            page: 'new',
            hasCategory: true,
            isQuickCreate: true
        };
        const customFields = new UntypedFormGroup({});
        this.customerForm.addControl('customFields',customFields);

    }

    ngOnInit(): void {
        this.localisationService.setDefaultLanguage();
        this.pickerAPIUrls = this.pickerService.pickerAPIUrls;
        if (this.formVisibility) {
            this.updateCreateFormVisibility();
        }
        this.propertyService.selectedOrganizationCustomerPickerFilterValue.subscribe((res)=>{            
            const organizationControl: AbstractControl = this.customerForm?.get('organization');
            if (!this.utilityService.isEmptyOrUndefined(res)) {
                this.orgParamObj['filter.organization_uid'] = res.organization_uid;
                this.prefillValues = {...this.prefillValues,organization : res.organization_uid};
            } else if(this.fromModule === 'PROPERTY'){
                delete this.orgParamObj?.['filter.organization_uid'];
                delete this.prefillValues?.['organization'];
                organizationControl?.enable();
                organizationControl?.setValue(null);
                this.organizations = this.getOrganizations('');
            }
          });
        this.timezones = this.timezoneService.timeZones;
        setTimeout(()=>{
            this.showCustomFields = true;
        }, 1000);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.formReset?.currentValue === true) {
            this.resetForm();
        }

        if (changes?.formSubmit?.firstChange === false && changes?.formSubmit?.currentValue === true) {
            this.submitQuickCreateForm();
        }

        if (changes?.formVisibility?.firstChange === false && changes?.formVisibility?.currentValue === true) {
            this.updateCreateFormVisibility();
        }
    }

    updateCreateFormVisibility(): void {
        if (!this.isFormLoaded) {
            this.isFormLoaded = true;
            this.openQuickCreateForm();
        } else {
            this.setOrganizationPrefillValue(this.organizationOptions);
        }
    }

    openQuickCreateForm(): void {
        this.updatePrefillValuesFilter();
        if(this.prefillValues?.property_address) {
            this.updateServiceAddress();
        }
        this.getData();
        this.customerForm.get('sameAddress').valueChanges.subscribe((value: any) => {
            if (!value && (this.fromModule == 'PROPERTY' && !this.prefillValues?.['organization'])) {
                this.updateBillingAddress();
            }
        });
        this.customerForm.get('category').valueChanges.subscribe((value: any)=>{
            if(!this.utilityService.isEmptyOrUndefined(value?.category_uid)){
                this.currentCategoryId = value?._id;
            }
        });
    }

    updatePrefillValuesFilter(): void {
        if (this.prefillValues) {
            Object.keys(this.prefillValues).forEach((key: string) => {
                if (key === 'organization' && this.prefillValues[key]) {
                    this.orgParamObj['filter.organization_uid'] = this.prefillValues[key];
                }
                
                // Handle mobile number prefill
                if (key === 'mobile' && this.prefillValues[key]) {
                        this.customerForm.get('mobile')?.get('phoneNumber')?.setValue(this.prefillValues[key]);
                    }
                });
        }
    }

    resetForm(): void {
        this.customerForm.reset({
            customer_timezone: this.prefillValues?.customer_timezone,
            sameAddress: true
        });
    }

    updateBillingAddress(): void {
        const serviceAddress: any = this.customerForm.get('serviceAddress').value;
        this.customerForm.get('billingAddress').patchValue(serviceAddress);
    }

    updateServiceAddress(): void {
        const serviceAddress: any = this.prefillValues?.property_address;
        this.customerForm?.get('serviceAddress').patchValue(serviceAddress)
    }

    getErrorMessageKey(field: CreateFormField): string {
        if (this.customerForm.get(field.formControlName).hasError('required')) {
            return field.validationTextKey;
        }

        if (field.inputType === 'email' && this.customerForm.get(field.formControlName).hasError('email')) {
            return field.emailValidationTextKey;
        }

        return '';
    }

    showError(formControlName: string): boolean {
        return this.customerForm.get(formControlName)?.touched && !this.customerForm.get(formControlName)?.valid;
    }

    checkFieldsContainerVisibility(header: CreateForm): boolean {
        if (header.isMatCheck) {
            return this.customerForm.get(header.formControlName) ? !this.customerForm.get(header.formControlName).value : true;
        }
        return true;
    }

    getData(): void {
        this.orgParamObj['page'] = 1;
        const urlParams = this.utilityService.constructParam(this.orgParamObj);
        const payload: any = {
            'sort_by': 'display_order'
          };
        forkJoin([
            this.dataCollectorService.getData(this.pickerAPIUrls.customerCategoryUrl, payload),
            this.dataCollectorService.getData(this.pickerAPIUrls.organizationUrl, urlParams)
        ]).subscribe(
            ([categories, organizations]: any) => {
                if (categories.type === 'success') {
                    this.categoryOptions = categories.data;
                    this.categories = this.customerForm.get('category').valueChanges.pipe(
                        startWith(''),
                        map((value: any) => this.categoryOptions)
                    );
                }
                if (organizations.type === 'success') {
                    this.pagination.loadingIndex = organizations.current_page;
                    this.pagination.totalPage = organizations.total_pages;
                    this.organizationOptions = organizations.data;
                    this.organizations = this.customerForm.get('organization').valueChanges.pipe(
                        startWith(''),
                        map((value: any) => this.organizationOptions)
                    );
                    this.setOrganizationPrefillValue(this.organizationOptions);
                }
                if(this.utilityService.isTimeZoneEnabled){
                    this.setTimezonePrefillValue();
                }
            }, (_error: any) => {
            });
    }

    setOrganizationPrefillValue(organizations: any[]): void {
        if (this.prefillValues?.['organization'] && organizations?.length) {
            setTimeout(() => {
                const organizationControl: AbstractControl = this.customerForm?.get('organization');
                organizationControl?.setValue(this.prefillValues['organization']);
                if(this.fromModule == 'PROPERTY') {
                    const sameAddressControl: AbstractControl = this.customerForm.get('sameAddress');
                    sameAddressControl?.setValue(false);
                    const billingAddressControl: AbstractControl = this.customerForm?.get('billingAddress');
                    billingAddressControl?.patchValue(organizations[0]?.organization_billing_address);
                    billingAddressControl?.get('zipcode')?.setValue(organizations[0]?.organization_billing_address?.zip_code || null);
                    billingAddressControl?.get('country')?.setValue(this.countriesList.find((cnt: any) => cnt.label === organizations[0]?.organization_billing_address?.country) || null);
                }
                else{
                    const { organization_address, organization_billing_address } = organizations[0] || {};

                    if (organization_address) {
                        this.updateOrgServiceAddress(organization_address);
                    }
                
                    if (organization_billing_address) {
                        this.updateOrgBillingAddress(organization_billing_address);
                    }
                }
                organizationControl?.disable();
            }, 100);
        }
    }

    setTimezonePrefillValue(): void {
        const timezoneControl: AbstractControl = this.customerForm?.get('customer_timezone');
        timezoneControl?.setValue(this.prefillValues?.customer_timezone);
    }

    getObservable(type: string): Observable<any[]> {
        if (type === 'category') {
            return this.categories;
        } else if (type === 'customer_timezone') {
            return this.timezones;
        } else {
            return this.organizations;
        }
    }

    onSearch(valueSubject: ReplaySubject<string | null>): void {
        this.updateOrganizations(valueSubject);
    }

    onClear(): void {
        this.organizations = this.getOrganizations('');
    }

    updateOrganizations(valueSubject: ReplaySubject<string | null>): void {
        if (valueSubject) {
            this.organizations = valueSubject.pipe(
                distinctUntilChanged(),
                debounceTime(250),
                filter(value => (value !== null && value !== undefined)),
                switchMap(term => this.getOrganizations(term))
            );
        }
    }

    getOrganizations(input: string): Observable<any[]> {
        this.pagination.keyword = input ? input : '';
        this.orgParamObj['filter.keyword'] = this.pagination.keyword;
        this.orgParamObj['page'] = 1;
        const urlParams = this.utilityService.constructParam(this.orgParamObj);
        this.loading = true;
        return this.dataCollectorService.getData(this.pickerAPIUrls.organizationUrl, urlParams)
            .pipe(
                catchError(() => of([])),
                map((response) => {
                    this.pagination.loadingIndex = response.current_page;
                    this.pagination.totalPage = response.total_pages;
                    this.organizationOptions = response.data;
                    this.loading = false;
                    return response.data;
                }),
            );
    }

    onScrollToEnd(): void {
        if (this.pagination.loadingIndex < this.pagination.totalPage) {
            this.pagination.loadingIndex++;
            this.fetchMore();
        }
    }

    fetchMore(): void {
        this.loading = true;
        this.orgParamObj['filter.keyword'] = this.pagination.keyword;
        this.orgParamObj['page'] = this.pagination.loadingIndex;
        const urlParams = this.utilityService.constructParam(this.orgParamObj);

        this.dataCollectorService.getData(this.pickerAPIUrls.organizationUrl, urlParams).subscribe(
            (organizations: any) => {
                this.loading = false;
                this.organizationOptions = this.organizationOptions.concat(organizations.data);
                this.organizations = this.customerForm.get('organization').valueChanges.pipe(
                    startWith(''),
                    map((value: any) => this.organizationOptions)
                );
            }, (_error: any) => {
                this.loading = false;
            });
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    getCountryByCode(formGroupName: string): CountryIso {
        const country: any = this.customerForm.get([formGroupName, 'country']).value;
        return this.countries.find(value => value.iso === country.iso);
    }

    getCountryCode(country: string): any {
        return this.countries.find(value => value.iso === country || (value.name?.toLowerCase()?.replaceAll(' ', '') === country?.toLowerCase()?.replaceAll(' ', '')));
    }

    submitQuickCreateForm(): void {
        this.customerForm.markAllAsTouched();
        this.customFieldService?.formBuilderSubscription.next(true);

        if (this.customerForm.get('sameAddress').value) {
            this.updateBillingAddress();
        }
        if (this.customerForm.valid) {
            this.createNewCustomer();
        } else {
            this.pickerService.scrollToFirstInvalidControl(this.eleRef.nativeElement);
            this.formValidationEmitter.emit(false);
        }
    }

    createNewCustomer(): void {
        const payLoad: any = this.getCustomerModel(this.customerForm.getRawValue());
        this.dataCollectorService.postData(this.pickerAPIUrls.newCustomerUrl, payLoad)
            .subscribe(
                (response: any) => {
                    this.toast.success(response.message);
                    this.dataCollectorService.getData(this.pickerAPIUrls.customerUrl + '/' + response.customer_uid).subscribe(
                        (customer: any) => {
                            this.eventEmitter.emit({ value: [customer.data] });
                        }, (_error: any) => {
                        });
                }, (_error: any) => {
                    this.formValidationEmitter.emit(false);
                }
            );
    }

    getCustomerModel(formValue: any): any {
        const customerAddress: any = this.contactInfoService.getContactInfoAddressModel(formValue['serviceAddress']);
        const billingAddress: any = this.contactInfoService.getContactInfoAddressModel(formValue['billingAddress']);
        const customFieldPayload = this.customFieldService.constructCustomFields(this.customerForm.controls['customFields'] as UntypedFormGroup,true, 'CUSTOMER');
        const allAddress: any[] = [customerAddress];
        if (!formValue.sameAddress) {
            allAddress.push(billingAddress);
        }
        return {
            customer: {
                custom_fields: customFieldPayload,
                customer_address: customerAddress,
                customer_all_addresses: allAddress,
                customer_billing_address: billingAddress,
                customer_category: formValue.category ? formValue.category.category_uid : null,
                customer_contact_no: {
                    home: formValue.home.phoneNumber ? (formValue.home.phoneNumber) : '',
                    mobile: formValue.mobile.phoneNumber ? (formValue.mobile.phoneNumber) : '',
                    work: formValue.work.phoneNumber ? (formValue.work.phoneNumber) : ''
                },
                customer_email: formValue.email,
                ...(this.utilityService.isTimeZoneEnabled && {'customer_timezone': formValue?.customer_timezone ?? null}),
                customer_first_name: formValue.firstName,
                customer_last_name: formValue.lastName,
                customer_organization: this.utilityService.hasModuleAccess('ORGANIZATION', false, 'from_sidebar') ? (formValue.organization || null) : null,
                customer_tags: []
            }
        };
    }

    // Method to handle picker selection
    onPickerSelect(selectedValue: any, fieldDetails: any): void {
        if (fieldDetails?.formControlName === 'organization' && selectedValue?.organization_uid) {
            const { organization_address, organization_billing_address } = selectedValue;
    
            if (organization_address) {
                this.updateOrgServiceAddress(organization_address);
            }
    
            if (organization_billing_address) {
                this.updateOrgBillingAddress(organization_billing_address);
            }
        }
    }

    // Method to update service address with organization address
    updateOrgServiceAddress(organization_address: any) {
        const serviceAddressControl = this.customerForm?.get('serviceAddress');
        if (!serviceAddressControl) return;
    
        serviceAddressControl.patchValue(organization_address);
        serviceAddressControl.get('zipcode')?.setValue(organization_address?.zip_code || null);
        serviceAddressControl.get('country')?.setValue(
            this.countriesList.find((cnt: any) => cnt.label === organization_address?.country) || null
        );
    }
    
    // Method to update billing address with organization billing address
    updateOrgBillingAddress(organization_billing_address: any) {
        const billingAddressControl = this.customerForm?.get('billingAddress');
        if (!billingAddressControl) return;
    
        this.customerForm?.get('sameAddress')?.setValue(false);
        billingAddressControl.patchValue(organization_billing_address);
        billingAddressControl.get('zipcode')?.setValue(organization_billing_address?.zip_code || null);
        billingAddressControl.get('country')?.setValue(
            this.countriesList.find((cnt: any) => cnt.label === organization_billing_address?.country) || null
        );
    }

    ngOnDestroy(): void {
        this.propertyService.selectedOrganizationCustomerPickerFilterValue.next(null);
    }
}
